import type { ReactNode } from "react";
import { createRef } from "react";

import { sendTelemetryEvent } from "@/lib/telemetry";
import type { IViewportWrapperRequiredProps } from "@/src/master/ViewportInjector";
import ViewportWrapper from "@/src/master/ViewportInjector";
import { Carousel } from "@Components/Carousel";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Loading } from "@Components/Loading";
import { SmartTileEventType, TelemetryEntity } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";

import MomentItem from "./MomentItem";
import type { ISpecialMomentsPanel } from "./SpecialMomentsPanel";
import { SpecialMomentsPanel } from "./SpecialMomentsPanel";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./SpecialMoments.module.css";

export interface ISpecialMoments extends IComponentAjax<ISpecialMomentsResult> {
    autoPlayEnable?: boolean;
    disableLazyLoading?: boolean;
    selectedFilter: number;
}

export interface ISpecialMomentsState extends IComponentAjaxState {
    index: number;
    lastIndex: number;
    isLoading: boolean;
    items: ISpecialMomentsPanel[];
    autoPlayEnable: boolean;
    isMobile: boolean;
}

interface ISpecialMomentsResult {
    items: ISpecialMomentsPanel[];
    startIndex: number;
    autoPlayEnable: boolean;
}

class SpecialMoments extends ComponentAjax<
    ISpecialMomentsResult,
    ISpecialMoments & IViewportWrapperRequiredProps,
    ISpecialMomentsState
> {
    public static readonly defaultProps: ISpecialMoments = {
        ...ComponentAjax.defaultProps,
        selectedFilter: 0,
    };

    constructor(props: ISpecialMoments & IViewportWrapperRequiredProps) {
        super(props);
        this.state = {
            index: 0,
            lastIndex: 0,
            isLoading: !this.props.initialData,
            items: this.props.initialData?.items ?? [],
            autoPlayEnable: true,
            isMobile: this.props.device === Device.Mobile,
        };
    }

    private panelRef = createRef<HTMLElement>();

    public UNSAFE_componentWillReceiveProps({ selectedFilter }: ISpecialMoments) {
        if (selectedFilter !== this.props.selectedFilter) {
            this.ajaxRequest();
            this.setState({ isLoading: true, isMobile: this.props.device === Device.Mobile });
        }
    }

    public componentDidMount(): void {
        if (this.state.items.length <= 0) {
            this.ajaxRequest();
            this.setState({ isLoading: true });
        } else {
            this.smartTileTelemetryRequest();
        }
    }

    public render(): JSX.Element | null {
        if (this.state.items.length <= 0) {
            if (this.state.isLoading === false) {
                return null;
            }
        }

        return (
            <section className={css.specialMomentsComponent} ref={this.panelRef}>
                {this.specialMoments()}
                {this.state.isLoading && <Loading />}
            </section>
        );
    }

    protected ajaxSuccess(response: ISpecialMomentsResult): void {
        if (!response) {
            this.setState({ items: [], isLoading: false, autoPlayEnable: false });
            return;
        }

        this.setState(
            {
                items: response.items,
                index: response.startIndex,
                lastIndex: response.startIndex,
                isLoading: false,
                autoPlayEnable: response.autoPlayEnable,
            },
            this.smartTileTelemetryRequest,
        );
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return false;
    }

    private createPanel = (panel: ISpecialMomentsPanel, index: number) => (
        <SpecialMomentsPanel
            key={index}
            {...panel}
            selected={index === this.state.index}
            hideTitleAndButton={this.detailsHidden(panel)}
        />
    );

    private specialMoments = () =>
        !this.state.isLoading && (
            <>
                {this.props.device === Device.Mobile ? null : (
                    <Carousel
                        selectedIndex={this.state.index}
                        showArrows={false}
                        autoplay={false}
                        className={css.topCarousel}
                    >
                        {this.state.items.map(this.createPanel)}
                    </Carousel>
                )}
                {this.getCarousel()}
            </>
        );

    private onSlide = (index: number) => {
        this.setState(
            (state) => ({
                lastIndex: state.index,
                index,
            }),
            this.smartTileTelemetryRequest,
        );
    };

    private smartTileTelemetryRequest = () => {
        if (!this.panelRef.current || document.hidden) {
            return;
        }

        const { bottom } = this.panelRef.current.getBoundingClientRect();

        if (bottom < 0) {
            return;
        }

        const itemId = this.state.items[this.state.index].id;

        sendTelemetryEvent({
            eventName: `${TelemetryEntity.SpecialMoment}-${SmartTileEventType.SmartTileView}`,
            actionType: "View",
            itemId: itemId,
            origin: "ForYou",
        });
    };

    private buildMomentItem = (panel: ISpecialMomentsPanel, index: number): ReactNode => (
        <MomentItem
            id={panel.id}
            key={index}
            index={index}
            linkUrl={panel.linkUrl}
            title={this.state.isMobile ? panel.titleMobile : panel.title || panel.titleMobile}
            imageUrl={panel.imageUrl}
            additionalInformation={panel.additionalInformation}
            color={panel.color}
            selected={index === this.state.index}
            isMobile={this.state.isMobile}
            hideTitleAndButton={this.detailsHidden(panel)}
        />
    );

    private getCarousel = (): JSX.Element => {
        const { isMobile } = this.state;
        const displayTime =
            (this.state.items &&
                this.state.items[this.state.index] &&
                this.state.items[this.state.index].displayTime) ||
            10000;

        return (
            <div className={`${cssPortal.standardContainer} ${css.carouselContainerMain}`}>
                <Carousel
                    className={css.momentCarousel}
                    selectedIndex={this.state.index}
                    slideIndex={!isMobile}
                    centerMode={isMobile}
                    onSlide={this.onSlide}
                    showArrows={!isMobile}
                    autoplay={this.state.autoPlayEnable}
                    autoplayInterval={displayTime}
                    infinite
                    autoplayPauseHover
                >
                    {this.state.items.map(this.buildMomentItem)}
                </Carousel>
            </div>
        );
    };

    private detailsHidden = (panel: ISpecialMomentsPanel) => {
        if (this.state.isMobile) {
            return false;
        }

        return !(panel.title?.trim() + panel.description?.trim());
    };
}

const SpecialMomentsWrapper = (props: ISpecialMoments) => (
    <ViewportWrapper Component={SpecialMoments} props={props} />
);

export default SpecialMomentsWrapper;
